'use client';

// primitives/icon-family.tsx — DS.3b PR-3 (ADR D-037 §2.4, D7). The icon-family render slot:
// a React context whose DEFAULT renderer reproduces today's Icon.tsx markup byte-for-byte (the
// material-symbols-rounded class, the same fontVariationSettings string, `{name}` as text
// content, aria-hidden), so a tree with no provider renders identically to pre-PR-3. A second
// skin (DS.4+) supplies a different family (e.g. Lucide) by mounting IconFamilyProvider with its
// own render function — no piece is touched. The FONT ITSELF stays the app's job to load
// (CLAUDE.md's `## Skins` section, §0.8c) — this file only decides what markup an icon NAME
// becomes, never which stylesheet backs it.

import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';

/** Preset icon sizes. A numeric px value is always accepted as an escape (D7). */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IconRenderOptions {
  /** The size as the caller wrote it — a preset name or a raw number. */
  size: IconSize | number;
  /** The resolved pixel value (opsz-parity rule: computed opsz === px, always). */
  px: number;
  filled: boolean;
  weight: number;
  className?: string;
  style?: CSSProperties;
}

export type IconRenderer = (name: string, opts: IconRenderOptions) => ReactNode;

/**
 * Default renderer — the EXACT JSX primitives/Icon.tsx emitted before PR-3 (D7). Byte-for-byte:
 * the `material-symbols-rounded` class, the same `fontVariationSettings` string, `{name}` as
 * text content, `aria-hidden`. A tree with no IconFamilyProvider renders identically.
 */
const defaultRenderer: IconRenderer = (name, { px, filled, weight, className = '', style }) => {
  const variationSettings = `'FILL' ${filled ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' ${px}`;

  return (
    <span
      className={`material-symbols-rounded ${className}`.trim()}
      style={{
        fontVariationSettings: variationSettings,
        fontSize: `${px}px`,
        ...style,
      }}
      aria-hidden="true"
    >
      {name}
    </span>
  );
};

const IconFamilyContext = createContext<IconRenderer>(defaultRenderer);

export function IconFamilyProvider({
  render,
  children,
}: {
  render: IconRenderer;
  children: ReactNode;
}) {
  return <IconFamilyContext.Provider value={render}>{children}</IconFamilyContext.Provider>;
}

export function useIconRenderer(): IconRenderer {
  return useContext(IconFamilyContext);
}
